// src/components/Dashboard/ClaimFlowPanel.jsx
//
// Panel del flujo de pasos en la ficha del reclamo. Organizado en pestañas
// por fase (Intake · Inspección · Cobertura · Cobro); cada paso es su propia
// tarjeta:
//   ✓ hecho      (con la fecha en que se salió de él)
//   ● actual     (abierta: su formulario, sus módulos y los botones para avanzar)
//   ○ pendiente
// Cualquier tarjeta se puede abrir para ver o corregir sus datos; los
// botones de avanzar solo están en el paso actual. Arriba, un resumen fijo
// (paso actual, días en el paso, "Mover a…"); abajo, el historial.
//
// Todo sale de config/claimFlow.js; los movimientos pasan por
// services/flowService.js.
import React, { useEffect, useMemo, useState, useId } from 'react';
import toast from 'react-hot-toast';
import { CLAIM_FLOW, CLAIM_PHASES, FINAL_STEPS, STEP_HELP } from '../../config/claimFlow';
import { getBlockers, getOptionBlockers, getStepTimer } from '../../config/flowRules';
import { useAuth } from '../../contexts/AuthContext';
import { canActOnStep, completeStep, getFlowHistory, getStep, getStepData, moveToStep } from '../../services/flowService';
import StepForm from './StepForm';
import ClosingBuilder from './ClosingBuilder';
import DocumentSignature from './DocumentSignature';
import AttorneyExportButton from './AttorneyExportButton';
import PolicyReview from './PolicyReview';
import PolDeductibleNote from './PolDeductibleNote';
import ChecksPanel from './ChecksPanel';
import { BlockerList, StepHelp, TimerBadge, TimerNotice, flowErrorMessage } from './FlowBits';
import './ClaimFlow.css';

// Módulos que ya tienen pantalla (se muestran dentro de la tarjeta o en la ficha).
const AVAILABLE_MODULES = {
  notes: ['Notas y solicitudes (en la ficha)', 'Notes & requests (on this page)'],
  documents: ['Documentos / adjuntos (en la ficha)', 'Documents / attachments (on this page)'],
  schedule: ['Agenda (en la ficha)', 'Schedule (on this page)'],
  checks: ['Cheques (en la ficha)', 'Checks (on this page)'],
  leadForm: ['Datos del reclamo: botón Editar reclamo', 'Claim details: Edit claim button']
};
// Módulos que todavía no existen.
const COMING_SOON = {
  checklist: ['Checklist de documentos', 'Document checklist'],
  scan: ['Escanear con la cámara', 'Camera scan'],
  deductibleCosts: ['Costos descontables', 'Deductible costs']
};
// Módulos que se dibujan dentro de la tarjeta (no llevan chip).
const INLINE_MODULES = ['stepForm', 'decision', 'signature', 'polGenerator', 'closing', 'attorneyExport', 'emailInsurer', 'policyReview', 'checks'];

const ACTOR_LABELS = {
  csr: ['Equipo CSR', 'CSR team'], pa: ['PA (decide)', 'PA (decides)'], client: ['Cliente', 'Client'],
  insurer: ['Aseguradora', 'Insurance company'], bank: ['Banco', 'Bank'], attorney: ['Abogado', 'Attorney'], system: ['Sistema', 'System']
};

// Correo al seguro con la LOR (paso "LOR enviada al seguro"): se abre en el
// programa de correo ya redactado; el PDF firmado se adjunta a mano (un
// enlace mailto no puede adjuntar archivos).
const insurerEmailLink = cr => {
  const to = cr.deskAdjusterEmail || cr.insuranceClaimEmail || '';
  const insureds = [cr.clientName, ...[cr.owner2, cr.owner3].filter(o => o && (o.firstName || o.lastName)).map(o => `${o.firstName || ''} ${o.lastName || ''}`.trim())].filter(Boolean).join(' & ');
  const subject = `Letter of Representation - Claim ${cr.claimNumber || ''} - ${insureds}`;
  const body = [
    `Hello${cr.deskAdjusterName ? ` ${cr.deskAdjusterName}` : ''},`,
    '',
    'Please find attached the signed Letter of Representation for the following claim:',
    '',
    `Insured: ${insureds}`,
    `Claim number: ${cr.claimNumber || ''}`,
    `Policy number: ${cr.policyNumber || ''}`,
    `Date of loss: ${cr.incidentDate || ''}`,
    `Loss address: ${cr.clientAddress || ''}`,
    '',
    'Please direct all communications regarding this claim to our office.',
    '',
    'Thank you,'
  ].join('\n');
  const query = `subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  return {
    to, subject, body,
    href: `mailto:${encodeURIComponent(to)}?${query}`,
    // mailto no hace nada si el equipo no tiene programa de correo predeterminado
    // (correo web): Gmail se abre redactado desde el navegador.
    gmailHref: `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(to)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  };
};

const copyInsurerEmail = (mail, es) => {
  const text = `${es ? 'Para' : 'To'}: ${mail.to}\n${es ? 'Asunto' : 'Subject'}: ${mail.subject}\n\n${mail.body}`;
  const done = () => toast.success(es ? 'Correo copiado. Pégalo en tu correo y adjunta la LOR firmada.' : 'Email copied. Paste it in your mail and attach the signed LOR.');
  const failed = () => toast.error(es ? 'No se pudo copiar. Selecciona el texto a mano.' : 'Could not copy. Select the text manually.');
  return navigator.clipboard?.writeText ? navigator.clipboard.writeText(text).then(done, failed) : failed();
};

const toDate = value => (value?.toDate ? value.toDate() : value ? new Date(value) : null);
const daysSince = value => {
  const date = toDate(value);
  return date ? Math.max(0, Math.floor((Date.now() - date.getTime()) / 86400000)) : null;
};
const shortDate = (value, es) => toDate(value)?.toLocaleDateString(es ? 'es' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric' }) || '';

const ClaimFlowPanel = ({ claimRecord, isAdmin, language, onClaimChange }) => {
  const es = language === 'es';
  const t = pair => (es ? pair[0] : pair[1]);
  const label = s => (s ? t([s.label.es, s.label.en]) : '—');
  // Equipo CSR = Super Admin y CSR Assistant: hacen el proceso completo (documentos, correo al seguro,
  // closings); el cobro de CSR y el movimiento manual de pasos siguen siendo del Super Admin.
  const { userRoleKey } = useAuth();
  const isCsr = userRoleKey === 'csr_assistant';
  const isTeam = isAdmin || isCsr;
  // La asistente de P.A también genera, envía a firmar y sube firmados LOR/SPOL.
  const canSign = isTeam || userRoleKey === 'pa_assistant';
  const role = isAdmin ? 'admin' : isCsr ? 'csr_assistant' : 'adjuster';
  const roleKey = isAdmin ? 'super_admin' : userRoleKey;
  const [flow, setFlow] = useState(claimRecord?.flow || null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [moveError, setMoveError] = useState('');
  const moveId = useId();
  const [busy, setBusy] = useState(false);
  const [moveTarget, setMoveTarget] = useState('');
  const [moveReason, setMoveReason] = useState('');
  const currentStep = flow?.stepId ? getStep('claim', flow.stepId) : null;
  const currentPhase = typeof currentStep?.phase === 'number' ? currentStep.phase : null;
  const [activePhase, setActivePhase] = useState(currentPhase || 1);
  const [openSteps, setOpenSteps] = useState(() => new Set(currentStep ? [currentStep.id] : []));
  const [stepData, setStepData] = useState({}); // datos del paso actual (para plazos y bloqueos)
  const [checks, setChecks] = useState([]); // cheques del reclamo (para poder cerrarlo)

  useEffect(() => { setFlow(claimRecord?.flow || null); }, [claimRecord?.flow]);

  const loadHistory = () => {
    if (!claimRecord?.id) return;
    getFlowHistory('claim', claimRecord.id).then(setHistory).catch(error => console.error('Error loading flow history:', error));
  };
  useEffect(loadHistory, [claimRecord?.id]);

  useEffect(() => {
    if (!claimRecord?.id || !flow?.stepId) { setStepData({}); return undefined; }
    let cancelled = false;
    getStepData('claim', claimRecord.id, flow.stepId)
      .then(data => { if (!cancelled) setStepData(data); })
      .catch(error => { console.error('Error loading step data:', error); if (!cancelled) setStepData({}); });
    return () => { cancelled = true; };
  }, [claimRecord?.id, flow?.stepId]);

  // Al cambiar de paso: se abre su pestaña y su tarjeta.
  useEffect(() => {
    if (!currentStep) return;
    if (typeof currentStep.phase === 'number') setActivePhase(currentStep.phase);
    setOpenSteps(prev => new Set([...prev, currentStep.id]));
  }, [currentStep?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pasos por los que ya se pasó → fecha en que se salió de ellos (la última).
  const exitedAt = useMemo(() => {
    const map = new Map();
    history.forEach(h => { if (h.fromStep) map.set(h.fromStep, h.at); });
    return map;
  }, [history]);

  if (!claimRecord) return null;

  const days = daysSince(flow?.enteredAt);
  const canAct = canActOnStep(currentStep, role);
  const record = { ...claimRecord, flow };
  const context = { stepData, checks };
  const timer = getStepTimer('claim', record, stepData);
  const blockers = currentStep ? getBlockers('claim', record, context) : [];
  const toggle = id => setOpenSteps(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const run = async action => {
    setBusy(true);
    try {
      const next = await action();
      setFlow(next);
      loadHistory();
      toast.success(es ? `Paso actual: ${getStep('claim', next.stepId).label.es}` : `Current step: ${getStep('claim', next.stepId).label.en}`);
    } catch (error) {
      console.error('Flow action failed:', error);
      toast.error(flowErrorMessage(error, es));
    } finally {
      setBusy(false);
    }
  };

  const complete = decisionId => run(() => completeStep({ type: 'claim', recordId: claimRecord.id, currentStepId: currentStep.id, decisionId, role, roleKey, context }));

  // LOR vencida (72 h sin enviarla al seguro): el documento del cliente pide
  // generarla y firmarla de nuevo. Se guarda la firmada vencida como historial.
  // Una sola transacción: la LOR vencida pasa al historial y el paso vuelve a
  // "Firma de la LOR" juntos (o nada, si falla).
  const redoExpiredLor = () => run(async () => {
    const signedDocuments = { ...(claimRecord.signedDocuments || {}), lor: { expired: { ...(claimRecord.signedDocuments?.lor || {}), expiredAt: new Date().toISOString() } } };
    const next = await moveToStep({
      type: 'claim', recordId: claimRecord.id, currentStepId: flow.stepId, toStepId: 'lor_signature', role, roleKey,
      reason: es ? 'LOR vencida: 72 h sin enviarla al seguro' : 'LOR expired: 72 h without sending it',
      extraFields: { signedDocuments }
    });
    if (onClaimChange) onClaimChange({ signedDocuments });
    return next;
  });
  const move = () => {
    if (!moveTarget) return;
    if (!moveReason.trim()) { setMoveError(es ? 'Indica el motivo del cambio' : 'Enter a reason'); return; }
    setMoveError('');
    run(() => moveToStep({ type: 'claim', recordId: claimRecord.id, currentStepId: flow?.stepId ?? null, toStepId: moveTarget, reason: moveReason, role, roleKey }))
      .then(() => { setMoveTarget(''); setMoveReason(''); });
  };

  const stepStatus = s => (s.id === currentStep?.id ? 'current' : exitedAt.has(s.id) ? 'done' : 'pending');

  // Contenido de la tarjeta de un paso.
  const stepBody = (s, isCurrent) => {
    const chips = s.modules.filter(m => !INLINE_MODULES.includes(m));
    return (
      <div className="flow-step-body">
        <div className="flow-step-owner">
          {es ? 'Le toca a' : 'Owner'}: <strong>{t(ACTOR_LABELS[s.actor] || [s.actor, s.actor])}</strong>
          {s.optional ? ` · ${es ? 'opcional' : 'optional'}` : ''}
        </div>
        <StepHelp help={STEP_HELP.claim[s.id]} es={es} />
        {isCurrent && (
          <TimerNotice timer={timer} es={es}>
            {timer?.key === 'claim:lor_sent' && timer.status === 'overdue' && isTeam && (
              <button type="button" className="cd-btn" disabled={busy} onClick={redoExpiredLor}>
                <i className="fa-solid fa-rotate-left"></i> {es ? 'Volver a "Firma de la LOR"' : 'Back to "LOR signature"'}
              </button>
            )}
          </TimerNotice>
        )}

        {chips.some(key => AVAILABLE_MODULES[key]) && (
          <div className="flow-chips">
            {chips.filter(key => AVAILABLE_MODULES[key]).map(key => {
              const available = AVAILABLE_MODULES[key];
              return (
                <span key={key} className={`cd-status-badge ${available ? 'cd-status-paid' : 'cd-status-pending'}`}>
                  {t(available || COMING_SOON[key] || [key, key])}{!available && ` · ${es ? 'Próximamente' : 'Coming soon'}`}
                </span>
              );
            })}
          </div>
        )}

        {s.modules.includes('policyReview') && (
          <PolicyReview claimRecord={claimRecord} language={language} onClaimChange={onClaimChange} />
        )}
        {s.modules.includes('stepForm') && s.fields?.length > 0 && (
          <StepForm claimRecord={claimRecord} step={s} language={language} onSaved={(changes, stepChanges) => {
            if (onClaimChange) onClaimChange(changes);
            if (isCurrent) setStepData(prev => ({ ...prev, ...stepChanges }));
          }} />
        )}
        {s.modules.includes('signature') && (
          <div className="flow-block">
            <div className="flow-block-label">{es ? 'LOR: generar → enviar a firmar → subir firmada' : 'LOR: generate → send → upload signed'}</div>
            <DocumentSignature claimRecord={claimRecord} docType="lor" language={language} isAdmin={canSign} onClaimChange={onClaimChange} />
          </div>
        )}
        {s.modules.includes('polGenerator') && (
          <div className="flow-block">
            {/* El deducible no se captura en este paso: viene del reclamo (Editar reclamo →
                Coberturas y deducible, importación de Organized o revisión de póliza) y la
                SPOL lo toma de ahí. Aquí solo se muestra para que se vea qué usará. */}
            <PolDeductibleNote claimRecord={claimRecord} language={language} />
            <div className="flow-block-label">{es ? 'SPOL: generar → enviar a firmar → subir firmada y notarizada' : 'SPOL: generate → send → upload signed & notarized'}</div>
            <DocumentSignature claimRecord={claimRecord} docType="spol" language={language} isAdmin={canSign} onClaimChange={onClaimChange} />
          </div>
        )}
        {isTeam && s.modules.includes('emailInsurer') && (() => {
          const mail = insurerEmailLink(claimRecord);
          return (
            <div className="flow-block">
              <div className="flow-actions">
                <a href={mail.href} className="cd-btn cd-btn-invoice"><i className="fa-solid fa-envelope"></i> {es ? 'Redactar correo al seguro con la LOR' : 'Draft email to insurance with the LOR'}</a>
                <a href={mail.gmailHref} target="_blank" rel="noopener noreferrer" className="cd-btn"><i className="fa-brands fa-google"></i> {es ? 'Abrir en Gmail' : 'Open in Gmail'}</a>
                <button type="button" className="cd-btn" onClick={() => copyInsurerEmail(mail, es)}><i className="fa-solid fa-copy"></i> {es ? 'Copiar correo' : 'Copy email'}</button>
              </div>
              <div className="flow-note">
                {mail.to
                  ? (es ? `Para: ${mail.to} · adjunta la LOR firmada antes de enviar.` : `To: ${mail.to} · attach the signed LOR before sending.`)
                  : (es ? 'Falta el correo del Desk Adjuster o de reclamos de la aseguradora (paso "Reporte al seguro" o Editar reclamo).' : 'Missing DA or insurance claims email.')}
              </div>
            </div>
          );
        })()}
        {isTeam && s.modules.includes('attorneyExport') && (
          <div className="flow-block"><AttorneyExportButton claimRecord={claimRecord} language={language} /></div>
        )}
        {s.modules.includes('checks') && (
          <ChecksPanel claimRecord={claimRecord} isAdmin={isAdmin} language={language} onChecksChange={setChecks} />
        )}
        {s.modules.includes('closing') && (
          <ClosingBuilder claimRecord={claimRecord} language={language} isAdmin={isTeam} onClaimChange={onClaimChange} mode={isCurrent ? 'full' : 'list'} />
        )}

        {/* Avanzar: solo en el paso actual */}
        {isCurrent && s.kind !== 'final' && (
          canAct ? (
            <>
              <BlockerList blockers={blockers} es={es} />
              <div className="flow-actions">
                {s.options
                  ? s.options.map(option => {
                    const optionBlockers = getOptionBlockers('claim', record, option.id, context);
                    return (
                      <button key={option.id} type="button" className="cd-btn cd-btn-check" disabled={busy || blockers.length > 0 || optionBlockers.length > 0}
                        title={optionBlockers.map(b => (es ? b.es : b.en)).join(' ')} onClick={() => complete(option.id)}>
                        {t([option.label.es, option.label.en])}
                      </button>
                    );
                  })
                  : (
                    <button type="button" className="cd-btn cd-btn-check" disabled={busy || blockers.length > 0} onClick={() => complete()}>
                      <i className="fa-solid fa-check"></i> {es ? 'Listo, siguiente' : 'Done, next'}: {label(getStep('claim', s.next))}
                    </button>
                  )}
              </div>
              {s.options && s.options.map(option => {
                const optionBlockers = getOptionBlockers('claim', record, option.id, context);
                return optionBlockers.length > 0
                  ? <p key={option.id} className="flow-note"><strong>{t([option.label.es, option.label.en])}:</strong> {optionBlockers.map(b => (es ? b.es : b.en)).join(' ')}</p>
                  : null;
              })}
            </>
          ) : (
            <p className="flow-note">{es ? 'Este paso lo registra el equipo CSR.' : 'This step is recorded by the CSR team.'}</p>
          )
        )}
      </div>
    );
  };

  const stepCard = s => {
    const status = stepStatus(s);
    const isOpen = openSteps.has(s.id);
    const icon = status === 'done' ? <i className="fa-solid fa-check"></i> : status === 'current' ? <i className="fa-solid fa-circle" style={{ fontSize: '.5rem' }}></i> : null;
    return (
      <div key={s.id} className={`flow-step is-${status}`}>
        <button type="button" className="flow-step-head" onClick={() => toggle(s.id)} aria-expanded={isOpen}>
          <span className="flow-step-icon">{icon}</span>
          <span className="flow-step-title">{label(s)}</span>
          {status === 'done' && <span className="flow-step-date">{shortDate(exitedAt.get(s.id), es)}</span>}
          {status === 'current' && <span className="cd-status-badge cd-status-count">{es ? 'Actual' : 'Current'}</span>}
          <i className={`fa-solid fa-chevron-${isOpen ? 'up' : 'down'} flow-step-chevron`}></i>
        </button>
        {isOpen && stepBody(s, status === 'current')}
      </div>
    );
  };

  const phaseSteps = phaseId => CLAIM_FLOW.steps.filter(s => s.phase === phaseId);
  const isFinal = currentStep && FINAL_STEPS.includes(currentStep.id);

  return (
    <div className="cd-detail-panel flow-panel">
      <div className="cd-detail-panel-header">
        <h2><i className="fa-solid fa-route"></i> {es ? 'Pasos del reclamo' : 'Claim steps'}</h2>
      </div>

      {/* Resumen fijo */}
      {currentStep ? (
        <div className="flow-summary">
          <div>
            <div className="flow-summary-label">{isFinal ? (es ? 'Reclamo' : 'Claim') : (es ? 'Paso actual' : 'Current step')}</div>
            <div className="flow-summary-step">{label(currentStep)}</div>
            {!isFinal && <div className="flow-summary-owner">{es ? 'Le toca a' : 'Owner'}: {t(ACTOR_LABELS[currentStep.actor] || [currentStep.actor, currentStep.actor])}</div>}
          </div>
          <span className="flow-summary-badges">
            {timer && <TimerBadge timer={timer} es={es} />}
            <span className="cd-status-badge cd-status-count">
              {days === null ? (es ? 'Migrado · sin fecha de entrada' : 'Migrated · no entry date') : `${days} ${es ? (days === 1 ? 'día en este paso' : 'días en este paso') : (days === 1 ? 'day in this step' : 'days in this step')}`}
            </span>
          </span>
        </div>
      ) : (
        <p className="closing-warning">{es ? 'Este reclamo todavía no tiene un paso asignado (es anterior al flujo de pasos).' : 'This claim has no step yet (created before the step flow).'}</p>
      )}

      {/* Pestañas por fase */}
      {/* Pestañas accesibles: flechas izq./der. (e Inicio/Fin) cambian de fase. */}
      <div className="flow-tabs" role="tablist" aria-label={es ? 'Fases del reclamo' : 'Claim phases'}
        onKeyDown={event => {
          const ids = CLAIM_PHASES.map(p => p.id);
          const index = ids.indexOf(activePhase);
          const next = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: ids.length - 1 }[event.key];
          if (next === undefined) return;
          event.preventDefault();
          const id = ids[(next + ids.length) % ids.length];
          setActivePhase(id);
          document.getElementById(`flow-tab-${id}`)?.focus();
        }}>
        {CLAIM_PHASES.map(p => {
          const steps = phaseSteps(p.id);
          const done = steps.filter(s => stepStatus(s) === 'done').length;
          const isActive = activePhase === p.id;
          return (
            <button key={p.id} id={`flow-tab-${p.id}`} type="button" role="tab" aria-selected={isActive} aria-controls="flow-phase-panel"
              tabIndex={isActive ? 0 : -1} onClick={() => setActivePhase(p.id)}
              className={`flow-tab ${isActive ? 'active' : ''}`}>
              <span>{p.id}. {t([p.label.es, p.label.en])}{currentPhase === p.id && <span className="flow-tab-current" aria-label={es ? 'fase actual' : 'current phase'}></span>}</span>
              <span className="flow-tab-count">{done}/{steps.length} {es ? 'hechos' : 'done'}</span>
            </button>
          );
        })}
      </div>

      {/* Tarjetas de la fase elegida */}
      <div role="tabpanel" id="flow-phase-panel" aria-labelledby={`flow-tab-${activePhase}`}>
        {phaseSteps(activePhase).map(stepCard)}
      </div>

      {/* Corrección (mover a cualquier paso, con motivo) e historial: solo equipo CSR, plegado por defecto */}
      {isAdmin && (
      <details className="flow-advanced">
        <summary>{es ? 'Avanzado: corregir el paso e historial' : 'Advanced: fix the step and history'}</summary>
        <div className="flow-move">
          <select className="cc-select" value={moveTarget} onChange={e => setMoveTarget(e.target.value)} aria-label={es ? 'Mover a paso' : 'Move to step'}>
            <option value="">{currentStep ? (es ? 'Mover a otro paso…' : 'Move to another step…') : (es ? 'Asignar paso…' : 'Assign step…')}</option>
            {CLAIM_PHASES.map(p => (
              <optgroup key={p.id} label={`${p.id}. ${t([p.label.es, p.label.en])}`}>
                {phaseSteps(p.id).map(s => <option key={s.id} value={s.id}>{label(s)}</option>)}
              </optgroup>
            ))}
            <optgroup label={es ? 'Cierre' : 'Close'}>
              {CLAIM_FLOW.steps.filter(s => s.phase === 'end').map(s => <option key={s.id} value={s.id}>{label(s)}</option>)}
            </optgroup>
          </select>
          {moveTarget && (
            <>
              <input id={`${moveId}-reason`} className="cc-input" required aria-required="true" aria-label={es ? 'Motivo del cambio (obligatorio)' : 'Reason for the change (required)'}
                aria-invalid={moveError ? 'true' : undefined} aria-describedby={moveError ? `${moveId}-error` : undefined}
                placeholder={es ? 'Motivo (obligatorio)' : 'Reason (required)'} value={moveReason}
                onChange={e => { setMoveError(''); setMoveReason(e.target.value); }} />
              <button type="button" className="cd-btn" disabled={busy} onClick={move}>{es ? 'Mover' : 'Move'}</button>
              {moveError && <p id={`${moveId}-error`} role="alert" className="cc-field-error">{moveError}</p>}
            </>
          )}
        </div>
      </details>
      )}

      {/* Historial */}
      <button type="button" className="flow-history-toggle" onClick={() => setShowHistory(v => !v)}>
        <i className={`fa-solid fa-chevron-${showHistory ? 'up' : 'down'}`}></i> {es ? `Historial (${history.length})` : `History (${history.length})`}
      </button>
      {showHistory && (
        <ul className="flow-history">
          {history.length === 0 && <li className="flow-muted">{es ? 'Sin movimientos todavía.' : 'No moves yet.'}</li>}
          {history.map(h => (
            <li key={h.id}>
              {label(getStep('claim', h.fromStep))} → <strong>{label(getStep('claim', h.toStep))}</strong>
              {h.reason ? ` — ${h.reason}` : ''}
              <small>{toDate(h.at)?.toLocaleString(es ? 'es' : 'en-US')} · {h.byEmail}</small>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ClaimFlowPanel;
